import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../../ThemeContext";
import { Backdrop, Drawer } from "./PanelStyles";

/**
 * Drawer chrome shared by the Notifications and Active Users panels.
 *
 * Owns the whole header so both panels stay identical: one 52px bar carrying
 * the title, count and controls, and one row of tabs. Search replaces the bar
 * in place rather than adding a row, which keeps the header at two lines
 * instead of the four the old layout spent before any content appeared.
 *
 * Rendered through a portal on document.body so it sits outside the app's
 * styled `Wrapper`, where the global `.bm-*` rules live.
 */

const IconSearch = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
    <circle cx="7" cy="7" r="4.5" />
    <path d="M10.5 10.5 14 14" strokeLinecap="round" />
  </svg>
);

const IconClose = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
    <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
  </svg>
);

const PanelShell = ({
  open,
  onClose,
  title,
  count,
  countTone = "accent",
  tabs = [],
  activeTab,
  onTab,
  query,
  onQuery,
  searchPlaceholder = "Search",
  action,
  children,
}) => {
  const { themeState } = useTheme();
  const dark = themeState?.mode === "dark";
  const [finding, setFinding] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (finding) inputRef.current?.focus();
  }, [finding]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      // Escape backs out of search first, then closes the drawer.
      if (finding) {
        setFinding(false);
        onQuery?.("");
        return;
      }
      onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose, finding, onQuery]);

  // Leaving the panel resets it, so reopening never shows a stale filter.
  useEffect(() => {
    if (!open) {
      setFinding(false);
      onQuery?.("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return createPortal(
    <>
      <Backdrop open={open} dark={dark} onClick={onClose} aria-hidden="true" />
      <Drawer
        open={open}
        dark={dark}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        aria-hidden={!open}
      >
        {finding ? (
          <div className="np-find">
            <IconSearch />
            <input
              ref={inputRef}
              value={query || ""}
              onChange={(e) => onQuery?.(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
            />
            <button
              className="np-icon"
              onClick={() => {
                setFinding(false);
                onQuery?.("");
              }}
              aria-label="Close search"
            >
              <IconClose />
            </button>
          </div>
        ) : (
          <div className="np-bar">
            <h2 className="np-h">{title}</h2>
            {count != null && (
              <span className={`np-pill${countTone === "accent" ? "" : " quiet"}`}>
                {count}
              </span>
            )}
            <span className="np-spring" />
            {action}
            <button
              className="np-icon"
              onClick={() => setFinding(true)}
              aria-label="Search"
            >
              <IconSearch />
            </button>
            <button className="np-icon" onClick={onClose} aria-label="Close">
              <IconClose />
            </button>
          </div>
        )}

        {tabs.length > 0 && (
          <div className="np-seg" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.key}
                className="np-tab"
                role="tab"
                aria-selected={activeTab === t.key}
                onClick={() => onTab?.(t.key)}
              >
                {t.label}
                {t.n != null && <span className="np-c">{t.n}</span>}
              </button>
            ))}
          </div>
        )}

        <div className="np-list">{children}</div>
      </Drawer>
    </>,
    document.body
  );
};

export default PanelShell;
